import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { EntryTypeBlockStatus } from 'chaika-enums';
import { Optional } from 'chaika-frontend-types/frontend-cms';

import { SelectButtonMenuItem } from '@components/SelectButtonMenu';
import { EditEntryTypePageAttributes } from 'chaika-test-attributes';
import { assertIsDefined } from '@utils/assertIsDefined';
import { translate } from '@chaika/i18n/helper';

import { useActivateBlock, useCopyBlock, useCreateBlockDraft } from '../../../queries/blockMutations';
import { blockQueryOptions } from '../../../queries/blockQueries';

const BLOCK_REQUIRED_MESSAGE = 'Block version must be loaded before header actions are used';

interface Params {
    blockId: string;
    version: number;
}

interface Data {
    title: string;
    disabled: boolean;
    items: SelectButtonMenuItem[];
    onClick(): Promise<void>;
}

export function useBlockHeaderActions({ blockId, version }: Params): Data {
    const navigate = useNavigate();
    const { data: block } = useQuery(blockQueryOptions.version(blockId, version));
    const createBlockDraft = useCreateBlockDraft();
    const activateBlock = useActivateBlock();
    const copyBlock = useCopyBlock();

    const onCreateCopyClick = async (): Promise<void> => {
        assertIsDefined(block, BLOCK_REQUIRED_MESSAGE);

        const copy = await copyBlock.mutateAsync({ block });

        navigate(`/template/block/${copy.id}/version/${copy.version}`);
    };

    const onClick = async (): Promise<void> => {
        assertIsDefined(block, BLOCK_REQUIRED_MESSAGE);

        if (block.status === EntryTypeBlockStatus.Active) {
            const draft = await createBlockDraft.mutateAsync({ blockId, version, block });

            navigate(`/template/block/${blockId}/version/${draft.version}`);

            return;
        }

        await activateBlock.mutateAsync({ blockId, version });
    };

    return {
        title: getActionTitle(block?.status),
        disabled: (block?.fields.length ?? 0) === 0,
        items: [
            {
                label: translate('createCopy'),
                dataTest: EditEntryTypePageAttributes.CreateCopyButton,
                onClick: onCreateCopyClick,
            },
        ],
        onClick,
    };
}

function getActionTitle(status: Optional<EntryTypeBlockStatus>): string {
    switch (status) {
        case EntryTypeBlockStatus.Active:
            return translate('edit');
        case EntryTypeBlockStatus.Draft:
            return translate('publish');
        case EntryTypeBlockStatus.Archived:
            return translate('restore');
        default:
            return '';
    }
}
